import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, throwError, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { User } from '../models/user';
import { ResponseRequest } from '../response-request';
import { SecureStorageService } from './secure-storage.service';
import {
  StarTarifHistoryPagination,
  StarTarifHistoryResponse,
  StarTarifHistoryRow,
} from './star-tarif.service';

interface AnalyticsData {
  revenues: {
    labels: string[];
    values: number[];
    total: number;
    growth: number;
  };
  videos: {
    labels: string[];
    values: number[];
    total: number;
    growth: number;
  };
  categories: {
    labels: string[];
    values: number[];
  };
  performance: {
    labels: string[];
    values: number[];
  };
  topCelebrities: any[];
  topFans: any[];
}

interface HistoricalRate {
  id: number;
  from_currency: string;
  to_currency: string;
  rate: string;
  status: string;
  etat: string;
  created_at: string;
  updated_at: string;
}

interface HistoricalRatesResponse {
  success: boolean;
  data: HistoricalRate[];
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminDashboardService {
  apiUrl: string = environment.apiUrl;
  private token: string = '';
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': ''
    })
  };

  constructor(
    private http: HttpClient,
    private secureStorage: SecureStorageService
  ) {
    this.token = this.secureStorage.getItem<string>('token') || '';
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      })
    };
  }

  private getHeaders() {
    const token = this.secureStorage.getItem<string>('token') || '';
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      })
    };
  }

  getDashboardStats(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/stats`, this.httpOptions);
  }

  getCelebrities(page: number = 1, filters?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/allCelebrities`, {
      ...this.httpOptions,
      params: { page, ...filters }
    });
  }

  allCelebrities(page: number = 1, filters?: any): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', filters?.per_page || '10');

    if (filters?.search) {
      params = params.set('search', filters.search);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }

    return this.http.get(`${this.apiUrl}/admin/celebrities`, {
      ...this.httpOptions,
      params: params
    });
  }

  approveCelebrity(id: number): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/celebrities/${id}/approve`, {}, this.httpOptions);
  }

  suspendCelebrity(id: number): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/celebrities/${id}/suspend`, {}, this.httpOptions);
  }

  getVideos(filters?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/videos`, {
      ...this.httpOptions,
      params: filters
    });
  }

  moderateVideo(id: number, action: 'approve' | 'reject'): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/videos/${id}/${action}`, {}, this.httpOptions);
  }

  getFans(page: number = 1, filters?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/allFans`, {
      ...this.httpOptions,
      params: { page, ...filters }
    });
  }

  getVideoDemands(filters?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/getVideoDemands`, {
      ...this.httpOptions,
      params: filters
    });
  }

  getDemandDetails(id: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}/getVideoDemand/${id}`, {
      ...this.httpOptions
    });
  }

  getPosts(filters?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/posts`, {
      ...this.httpOptions,
      params: filters
    });
  }

  moderatePost(id: number, action: 'approve' | 'reject'): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/posts/${id}/${action}`, {}, this.httpOptions);
  }

  getChartData(type: string, params?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/charts/${type}`, {
      ...this.httpOptions,
      params
    });
  }

  getTopCelebrities(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/celebrities/top`, this.httpOptions);
  }

  getRevenueStats(period: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/stats/revenue`, { 
      params: { period },
      ...this.httpOptions
    });
  }

  getActivityLogs(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/activity-logs`, this.httpOptions);
  }

  getPayments(filters?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/payments`, {
      ...this.httpOptions,
      params: filters
    });
  }

  processPayment(id: number, action: 'approve' | 'reject'): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/payments/${id}/${action}`, {}, this.httpOptions);
  }

  getNotifications(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/notifications`, this.httpOptions);
  }

  markNotificationAsRead(id: number): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/notifications/${id}/read`, {}, this.httpOptions);
  }

  getAnalytics(period: string) {
    return this.http.get<AnalyticsData>(`${environment.apiUrl}/analytics?period=${period}`, this.httpOptions);
  }

  getHistoricalRates(): Observable<HistoricalRatesResponse> {
    return this.http.get<HistoricalRatesResponse>(
      `${environment.apiUrl}/currency/historical-rates`,
      this.httpOptions
    );
  }

  // Categories Management
  getCategories(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/categories`, this.httpOptions);
  }

  createCategory(data: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/categories`, data, this.httpOptions);
  }

  updateCategory(id: number, data: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/categories/${id}`, data, this.httpOptions);
  }

  deleteCategory(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/categories/${id}`, this.httpOptions);
  }

  getCategoriesAdmin(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/categories`, this.httpOptions);
  }

  updateCategoryAdmin(id: number, data: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/admin/categories/${id}`, data, this.httpOptions);
  }

  createCategoryAdmin(data: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/categories`, data, this.httpOptions);
  }

  updateCategoryStatusAdmin(id: number, status: 'active' | 'inactive'): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/categories/${id}/status`, { status }, this.httpOptions);
  }

  deleteCategoryAdmin(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/admin/categories/${id}`, this.httpOptions);
  }

  updateCelebrityStatus(id: number, status: 'active' | 'inactive'): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/${id}/status`, { status }, this.httpOptions);
  }

  officializeCelebrityAccount(id: number, official: 1 | 0): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/${id}/officialize`, { official }, this.httpOptions);
  }

  getCelebrityDetails(id: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/${id}`, this.httpOptions);
  }

  /**
   * Historique des overrides de tarifs d'une célébrité (admin uniquement).
   * GET /api/admin/celebrities/{id}/star-tarif-history
   */
  getAdminStarTarifHistory(
    celebrityId: number,
    params?: { page?: number; per_page?: number; type_request?: string }
  ): Observable<{
    items: StarTarifHistoryRow[];
    pagination: StarTarifHistoryPagination | null;
  }> {
    let httpParams = new HttpParams();
    if (params?.page != null && params.page > 0) {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (params?.per_page != null && params.per_page > 0) {
      httpParams = httpParams.set('per_page', String(params.per_page));
    }
    if (params?.type_request != null && params.type_request !== '') {
      httpParams = httpParams.set('type_request', params.type_request);
    }

    return this.http
      .get<StarTarifHistoryResponse>(
        `${environment.apiUrl}/admin/celebrities/${celebrityId}/star-tarif-history`,
        { ...this.getHeaders(), params: httpParams }
      )
      .pipe(
        map((res) => ({
          items: res?.success && Array.isArray(res.data) ? res.data : [],
          pagination: res?.success && res.pagination ? res.pagination : null,
        }))
      );
  }

  updateFanStatus(id: number, status: 'active' | 'inactive' | 'blocked'): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/${id}/status`, { status }, this.httpOptions);
  }

  getLogCategories(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/logs/categories`, this.httpOptions);
  }

  getLogs(filters?: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/logs`, {
      ...this.httpOptions,
      params: filters
    });
  }

  downloadLogs(filters?: any): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/admin/logs/download`, {
      ...this.httpOptions,
      params: filters,
      responseType: 'blob'
    });
  }

  getSystemSettings(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/admin/settings/system`, this.httpOptions);
  }

  updateSystemSettings(settings: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/admin/settings/system`, settings, this.httpOptions);
  }

  updateDemandStatus(id: number, status: 'accepted' | 'rejected'): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/video-demands/${id}/status`, { status }, this.httpOptions);
  }

  deleteCelebrity(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/celebrities/${id}`, this.httpOptions);
  }

  register(user: User): Observable<ResponseRequest> {
    return this.http.post<ResponseRequest>(environment.apiUrl+'/users', user, this.httpOptions);
  }

  saveTarif(tarif: any): Observable<any> {
    const payload = {
      category_id: tarif.category_id,
      type_request: tarif.type_request,
      amount: Number(tarif.amount),
      currency: tarif.currency
    };

    return this.http.post(`${environment.apiUrl}/tarifs`, payload, this.httpOptions).pipe(
      catchError(error => {
        if (error.status === 400) {
          console.error('Erreur de validation:', error.error.errors);
        }
        return throwError(() => error);
      })
    );
  }

  updateTarif(id: number, tarif: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/tarifs/${id}`, tarif, this.httpOptions);
  }

  getTarifs(categoryId: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}/tarifs/category/${categoryId}`, this.httpOptions);
  }

  saveTarifs(tarifs: any[]): Observable<any> {
    return this.http.post(`${environment.apiUrl}/tarifs/batch`, { tarifs }, this.httpOptions);
  }

  deleteTarif(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/tarifs/${id}`, this.httpOptions);
  }

  getTarifsByCategory(categoryId: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}/tarifsByCategory/${categoryId}`, this.httpOptions).pipe(
      tap(response => console.log('Réponse brute API:', response)),
      catchError(error => {
        console.error('Erreur API:', error);
        return throwError(() => error);
      })
    );
  }

  getCategory(id: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}/categories/${id}`, this.httpOptions).pipe(
      catchError(error => {
        console.error('Erreur lors du chargement de la catégorie:', error);
        return throwError(() => error);
      })
    );
  }
} 